import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import SendIcon from "@material-ui/icons/Send";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import api from "../../services/api";
import { getBackendUrl } from "../../config";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import { buildTicketsPath } from "../../utils/ticketNavigation";

// ──────────────────────────────────────────────
// Reducer
// ──────────────────────────────────────────────
const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const idx = state.findIndex((c) => c.id === contact.id);
      if (idx !== -1) {
        state[idx] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const idx = state.findIndex((c) => c.id === contact.id);
    if (idx !== -1) {
      state[idx] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }
  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const idx = state.findIndex((c) => c.id === contactId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const emptyEpoForm = {
  name: "",
  lastName: "",
  number: "",
  email: "",
  country: "",
  city: "",
  address: "",
  zip: "",
  jobTitle: "",
  company: "",
  tipologia: "",
  isPartnerFidelizzato: false,
  description: "",
  linkedinUrl: "",
  internalReferent: "",
  projectIds: [],
};

// ──────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflow: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  epoChip: {
    marginLeft: 8,
    backgroundColor: "#1976d2",
    color: "#fff",
    height: 18,
    fontSize: 10,
  },
  partnerOk: { color: "#388e3c", fontSize: 16 },
  partnerMissing: { color: "#f57c00", fontSize: 16 },
  partnerChipOk: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #a5d6a7",
    fontWeight: 600,
    fontSize: 11,
  },
  partnerChipMissing: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffcc80",
    fontWeight: 600,
    fontSize: 11,
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
    },
  },
  filterSearch: {
    minWidth: 220,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "100%",
    },
  },
  filterControl: {
    minWidth: 160,
    "& .MuiSelect-root, & .MuiSelect-selectMenu": { fontStyle: "normal" },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "100%",
    },
  },
  actionButton: {
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  responsiveTable: {
    minWidth: 980,
  },
  folderAccordion: {
    background: "rgba(255,255,255,0.02)",
    marginBottom: theme.spacing(1.25),
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  folderSummary: {
    minHeight: 56,
    "& .MuiAccordionSummary-content": {
      margin: "8px 0",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  },
  folderTitle: {
    fontWeight: 600,
  },
  folderMeta: {
    opacity: 0.8,
    fontSize: 12,
  },
  folderDetails: {
    display: "block",
    paddingTop: 0,
  },
  folderList: {
    width: "100%",
    paddingTop: 0,
    paddingBottom: 0,
  },
  contactRow: {
    borderTop: "1px solid rgba(255,255,255,0.08)",
    paddingLeft: 0,
    paddingRight: 0,
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  contactPrimary: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contactActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      width: "100%",
      justifyContent: "flex-end",
    },
  },
  destinationDialogField: {
    minWidth: 280,
    width: "100%",
    marginTop: theme.spacing(1),
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  fullSpan: { gridColumn: "1 / -1" },
  avatarContainer: {
    position: "relative",
    width: 80,
    height: 80,
    cursor: "pointer",
    margin: "0 auto",
    gridColumn: "1 / -1",
    justifySelf: "center",
  },
  avatarLarge: { width: 80, height: 80 },
  cameraOverlay: {
    position: "absolute",
    bottom: 0,
    right: 0,
    background: "rgba(0,0,0,0.55)",
    borderRadius: "50%",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
const resolvePhotoUrl = (profilePicUrl) => {
  if (!profilePicUrl) return undefined;
  if (profilePicUrl.startsWith("http")) return profilePicUrl;
  return `${getBackendUrl()}${profilePicUrl}`;
};

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const isEpo = user?.profile === "epo";

  // Shared state
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [hasMore, setHasMore] = useState(false);

  // Normal contact modal state
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // EPO state
  const [tipologieOptions, setTipologieOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [tipologiaFilter, setTipologiaFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [epoModalOpen, setEpoModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [epoForm, setEpoForm] = useState(emptyEpoForm);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Non-EPO label filter
  const [epoLabelFilter, setEpoLabelFilter] = useState("all");

  // EPO selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingVCards, setSendingVCards] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDestination, setSendDestination] = useState("manager");

  // Load tipologie (EPO only)
  useEffect(() => {
    if (!isEpo) return;
    api
      .get("/hubspot/properties")
      .then(({ data }) => {
        const filtered = (data.tipologie || []).filter((t) =>
          /stakeholder|partner/i.test(t.label)
        );
        setTipologieOptions(filtered.length > 0 ? filtered : data.tipologie || []);
      })
      .catch(() => {});
  }, [isEpo]);

  useEffect(() => {
    if (!isEpo) return;
    api
      .get("/projects", { params: { pageNumber: 1, searchParam: "" } })
      .then(({ data }) => setProjectOptions(data.projects || []))
      .catch(() => {});
  }, [isEpo]);

  // Reset on search change
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, companyFilter, projectFilter, epoLabelFilter]);

  // Auto-open new contact form if navigated with openNew state
  useEffect(() => {
    if (isEpo && location.state?.openNew) {
      openEpoCreate();
      history.replace(location.pathname, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch contacts
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/", {
          params: {
            searchParam,
            pageNumber,
            ...(isEpo && companyFilter ? { company: companyFilter } : {}),
            ...(isEpo && projectFilter ? { projectId: projectFilter } : {}),
            ...(!isEpo && epoLabelFilter !== "all" ? { createdByProfile: epoLabelFilter } : {}),
          },
        });
        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, pageNumber, companyFilter, projectFilter, epoLabelFilter, isEpo]);

  // Socket
  useEffect(() => {
    const socket = openSocket();
    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        const c = data.contact;
        // Mirror the same exclusions applied by the backend API:
        // – never show groups
        // – EPO users only see contacts they created
        if (c.isGroup) return;
        if (isEpo && c.createdByUserId !== user.id) return;
        dispatch({ type: "UPDATE_CONTACTS", payload: c });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });
    return () => socket.disconnect();
  }, [isEpo, user.id]);

  // Client-side filters
  const visibleContacts = isEpo
    ? contacts.filter((c) => {
        if (tipologiaFilter && c.tipologia !== tipologiaFilter) return false;
        if (partnerFilter === "yes" && !c.isPartnerFidelizzato) return false;
        if (partnerFilter === "no" && c.isPartnerFidelizzato) return false;
        if (projectFilter) {
          const ids = (c.projects || []).map((p) => String(p.id));
          if (!ids.includes(String(projectFilter))) return false;
        }
        return true;
      })
    : contacts;

  // Handlers: normal
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        userId: user?.id,
        status: "open",
      });
      history.push(
        buildTicketsPath({
          ticketId: ticket.id,
          whatsappId: ticket.whatsappId,
          search: location.search,
        })
      );
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  // Handlers: EPO
  const openEpoCreate = () => {
    setEditingContact(null);
    setEpoForm(emptyEpoForm);
    setPhotoFile(null);
    setPhotoPreview(null);
    setEpoModalOpen(true);
  };

  const openEpoEdit = (contact) => {
    setEditingContact(contact);
    setEpoForm({
      name: contact.name || "",
      lastName: contact.lastName || "",
      number: contact.number || "",
      email: contact.email || "",
      country: contact.country || "",
      city: contact.city || "",
      address: contact.address || "",
      zip: contact.zip || "",
      jobTitle: contact.jobTitle || "",
      company: contact.company || "",
      tipologia: contact.tipologia || "",
      isPartnerFidelizzato: contact.isPartnerFidelizzato || false,
      description: contact.description || "",
      linkedinUrl: contact.linkedinUrl || "",
      internalReferent: contact.internalReferent || "",
      projectIds: (contact.projects || []).map((project) => project.id),
    });
    setPhotoFile(null);
    setPhotoPreview(resolvePhotoUrl(contact.profilePicUrl) || null);
    setEpoModalOpen(true);
  };

  const handleEpoClose = () => {
    setEpoModalOpen(false);
    setEditingContact(null);
    setEpoForm(emptyEpoForm);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleEpoDelete = async (contactId) => {
    if (!window.confirm(i18n.t("epoContacts.confirmDelete"))) return;
    try {
      await api.delete(`/epo/contacts/${contactId}`);
      dispatch({ type: "DELETE_CONTACT", payload: +contactId });
      toast.success(i18n.t("epoContacts.deleteSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleEpoFieldChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEpoForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "isPartnerFidelizzato" && !value ? { description: "" } : {}),
    }));
  };

  const handlePhotoClick = () => fileInputRef.current && fileInputRef.current.click();

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleEpoSave = async () => {
    setSaving(true);
    try {
      let savedContact;
      const isNew = !editingContact;
      if (editingContact) {
        const { data } = await api.put(`/epo/contacts/${editingContact.id}`, epoForm);
        savedContact = data;
      } else {
        const { data } = await api.post("/epo/contacts", epoForm);
        savedContact = data;
      }
      if (photoFile && savedContact && savedContact.id) {
        const formData = new FormData();
        formData.append("photo", photoFile);
        await api.post(`/epo/contacts/${savedContact.id}/photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // Send VCard to group only on creation, after photo is uploaded
      if (isNew && savedContact && savedContact.id) {
        try {
          await api.post(`/epo/contacts/${savedContact.id}/send-to-group`);
        } catch (err) {
          console.error(`Failed to send VCard to group: ${err}`);
        }
      }
      toast.success(i18n.t("epoContacts.form.success"));
      handleEpoClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  // Pagination
  const loadMore = () => setPageNumber((p) => p + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  // EPO checkbox selection
  const toggleSelectContact = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visibleContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleContacts.map((c) => c.id)));
    }
  };

  const handleSendVCards = async () => {
    if (selectedIds.size === 0) return;
    setSendingVCards(true);
    try {
      const { data } = await api.post("/epo/contacts/send-vcards", {
        contactIds: Array.from(selectedIds),
        destination: sendDestination,
      });
      toast.success(
        `${i18n.t("epoContacts.sendVCards.success")}: ${data.sent} inviata/e${data.errors > 0 ? `, ${data.errors} errori` : ""}`
      );
      setSelectedIds(new Set());
      setSendDialogOpen(false);
    } catch (err) {
      toastError(err);
    }
    setSendingVCards(false);
  };

  const folderGroups = (() => {
    if (!isEpo) return [];

    const groups = projectOptions
      .map((project) => ({
        id: `project-${project.id}`,
        projectId: project.id,
        name: project.name,
        contacts: visibleContacts.filter((contact) =>
          (contact.projects || []).some((p) => p.id === project.id)
        ),
      }))
      .filter((group) => group.contacts.length > 0);

    const unassigned = visibleContacts.filter(
      (contact) => !contact.projects || contact.projects.length === 0
    );

    if (unassigned.length > 0) {
      groups.push({
        id: "unassigned",
        projectId: null,
        name: i18n.t("epoContacts.accordion.unassigned"),
        contacts: unassigned,
      });
    }

    return groups;
  })();

  const toggleSelectFolder = (folderContacts) => {
    const folderIds = folderContacts.map((contact) => contact.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = folderIds.every((id) => next.has(id));
      if (allSelected) {
        folderIds.forEach((id) => next.delete(id));
      } else {
        folderIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const companyOptions = Array.from(
    new Set(contacts.map((c) => c.company).filter(Boolean))
  );

  // Partner status chip+icon
  const PartnerStatusChip = ({ contact }) => {
    if (!contact.isPartnerFidelizzato) return null;
    if (contact.description) {
      return (
        <Tooltip title={i18n.t("epoContacts.partnerStatus.withNotes")}>
          <Chip
            size="small"
            icon={<CheckCircleIcon className={classes.partnerOk} />}
            label={i18n.t("epoContacts.partnerStatus.chipOk")}
            className={classes.partnerChipOk}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={i18n.t("epoContacts.partnerStatus.missingNotes")}>
        <Chip
          size="small"
          icon={<HourglassEmptyIcon className={classes.partnerMissing} />}
          label={i18n.t("epoContacts.partnerStatus.chipMissing")}
          className={classes.partnerChipMissing}
        />
      </Tooltip>
    );
  };

  return (
    <MainContainer>
      {!isEpo && (
        <>
          <ContactModal
            open={contactModalOpen}
            onClose={handleCloseContactModal}
            aria-labelledby="form-dialog-title"
            contactId={selectedContactId}
          />
          <ConfirmationModal
            title={
              deletingContact
                ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
                : i18n.t("contacts.confirmationModal.importTitlte")
            }
            open={confirmOpen}
            onClose={setConfirmOpen}
            onConfirm={() =>
              deletingContact
                ? handleDeleteContact(deletingContact.id)
                : handleimportContact()
            }
          >
            {deletingContact
              ? i18n.t("contacts.confirmationModal.deleteMessage")
              : i18n.t("contacts.confirmationModal.importMessage")}
          </ConfirmationModal>
        </>
      )}

      <MainHeader>
        <Title>
          {isEpo ? i18n.t("epoContacts.title") : i18n.t("contacts.title")}
        </Title>
        <MainHeaderButtonsWrapper>
          {isEpo ? (
            <div className={classes.filterBar}>
              <TextField
                className={classes.filterSearch}
                placeholder={i18n.t("epoContacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <Select
                  value={tipologiaFilter}
                  onChange={(e) => setTipologiaFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>{i18n.t("epoContacts.filters.allTipologie")}</em></MenuItem>
                  {tipologieOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <Select
                  value={partnerFilter}
                  onChange={(e) => setPartnerFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">{i18n.t("epoContacts.filters.allPartner")}</MenuItem>
                  <MenuItem value="yes">{i18n.t("epoContacts.filters.onlyPartner")}</MenuItem>
                  <MenuItem value="no">{i18n.t("epoContacts.filters.notPartner")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <Select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>{i18n.t("epoContacts.filters.allCompanies")}</em></MenuItem>
                  {companyOptions.map((company) => (
                    <MenuItem key={company} value={company}>{company}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <Select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>{i18n.t("epoContacts.filters.allProjects")}</em></MenuItem>
                  {projectOptions.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button className={classes.actionButton} variant="contained" color="primary" onClick={openEpoCreate}>
                {i18n.t("epoContacts.buttons.add")}
              </Button>
              <Button
                className={classes.actionButton}
                variant="contained"
                color="secondary"
                onClick={() => setSendDialogOpen(true)}
                disabled={selectedIds.size === 0 || sendingVCards}
                startIcon={sendingVCards ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                {`${i18n.t("epoContacts.buttons.sendVCards")}${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
              </Button>
            </div>
          ) : (
            <>
              <TextField
                className={classes.filterSearch}
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <Select
                  value={epoLabelFilter}
                  onChange={(e) => setEpoLabelFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">{i18n.t("contacts.filters.allEpoLabel")}</MenuItem>
                  <MenuItem value="epo">{i18n.t("contacts.filters.onlyEpo")}</MenuItem>
                  <MenuItem value="non-epo">{i18n.t("contacts.filters.notEpo")}</MenuItem>
                </Select>
              </FormControl>
              <Button
                className={classes.actionButton}
                variant="contained"
                color="primary"
                onClick={() => setConfirmOpen(true)}
              >
                {i18n.t("contacts.buttons.import")}
              </Button>
              <Button
                className={classes.actionButton}
                variant="contained"
                color="primary"
                onClick={handleOpenContactModal}
              >
                {i18n.t("contacts.buttons.add")}
              </Button>
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        {isEpo ? (
          <>
            {folderGroups.map((group) => {
              const folderIds = group.contacts.map((contact) => contact.id);
              const selectedInFolder = folderIds.filter((id) => selectedIds.has(id)).length;
              const folderChecked = folderIds.length > 0 && selectedInFolder === folderIds.length;
              const folderIndeterminate = selectedInFolder > 0 && selectedInFolder < folderIds.length;

              return (
                <Accordion key={group.id} defaultExpanded className={classes.folderAccordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.folderSummary}>
                    <Checkbox
                      checked={folderChecked}
                      indeterminate={folderIndeterminate}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelectFolder(group.contacts)}
                      size="small"
                    />
                    <Typography className={classes.folderTitle}>{group.name}</Typography>
                    <Typography className={classes.folderMeta}>
                      {i18n.t("epoContacts.accordion.count", { count: group.contacts.length })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.folderDetails}>
                    <List className={classes.folderList}>
                      {group.contacts.map((contact) => (
                        <ListItem key={`${group.id}-${contact.id}`} className={classes.contactRow}>
                          <div className={classes.contactPrimary}>
                            <Checkbox
                              checked={selectedIds.has(contact.id)}
                              onChange={() => toggleSelectContact(contact.id)}
                              size="small"
                            />
                            <ListItemAvatar>
                              <Avatar src={resolvePhotoUrl(contact.profilePicUrl)} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${contact.name}${contact.lastName ? ` ${contact.lastName}` : ""}`}
                              secondary={`${contact.number || ""} · ${contact.email || "-"} · ${contact.tipologia || "-"}`}
                            />
                            <PartnerStatusChip contact={contact} />
                          </div>
                          <div className={classes.contactActions}>
                            <IconButton size="small" onClick={() => openEpoEdit(contact)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEpoDelete(contact.id)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </div>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {folderGroups.length === 0 && !loading && (
              <Typography variant="body2" style={{ opacity: 0.8 }}>
                {i18n.t("epoContacts.empty")}
              </Typography>
            )}
            {loading && <TableRowSkeleton avatar columns={4} />}
          </>
        ) : (
          <Table className={classes.responsiveTable}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                {isEpo && (
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < visibleContacts.length}
                    checked={visibleContacts.length > 0 && selectedIds.size === visibleContacts.length}
                    onChange={toggleSelectAll}
                    size="small"
                  />
                )}
              </TableCell>
              <TableCell>
                {i18n.t(isEpo ? "epoContacts.table.name" : "contacts.table.name")}
              </TableCell>
              {isEpo ? (
                <>
                  <TableCell>{i18n.t("epoContacts.table.number")}</TableCell>
                  <TableCell>{i18n.t("epoContacts.table.company")}</TableCell>
                  <TableCell>{i18n.t("epoContacts.table.projects")}</TableCell>
                  <TableCell>{i18n.t("epoContacts.table.tipologia")}</TableCell>
                  <TableCell>{i18n.t("epoContacts.table.email")}</TableCell>
                  <TableCell align="center">{i18n.t("epoContacts.table.partner")}</TableCell>
                </>
              ) : (
                <>
                  <TableCell align="center">{i18n.t("contacts.table.whatsapp")}</TableCell>
                  <TableCell align="center">{i18n.t("contacts.table.email")}</TableCell>
                </>
              )}
              <TableCell align="center">
                {i18n.t(isEpo ? "epoContacts.table.actions" : "contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {visibleContacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  selected={isEpo && selectedIds.has(contact.id)}
                >
                  <TableCell padding="checkbox">
                    {isEpo ? (
                      <Checkbox
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelectContact(contact.id)}
                        size="small"
                      />
                    ) : (
                      <Avatar src={resolvePhotoUrl(contact.profilePicUrl)} />
                    )}
                  </TableCell>
                  <TableCell>
                    {isEpo && (
                      <Avatar
                        src={resolvePhotoUrl(contact.profilePicUrl)}
                        style={{ display: "inline-flex", width: 28, height: 28, marginRight: 8, verticalAlign: "middle" }}
                      />
                    )}
                    {contact.name}
                    {contact.lastName ? ` ${contact.lastName}` : ""}
                    {!isEpo && contact.createdByProfile === "epo" && (
                      <Chip label="EPO" size="small" className={classes.epoChip} />
                    )}
                  </TableCell>
                  {isEpo ? (
                    <>
                      <TableCell>{contact.number}</TableCell>
                      <TableCell>{contact.company || "—"}</TableCell>
                      <TableCell>
                        {(contact.projects || []).length > 0
                          ? contact.projects.map((project) => project.name).join(", ")
                          : "—"}
                      </TableCell>
                      <TableCell>{contact.tipologia || "—"}</TableCell>
                      <TableCell>{contact.email || "—"}</TableCell>
                      <TableCell align="center">
                        <PartnerStatusChip contact={contact} />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center">{contact.number}</TableCell>
                      <TableCell align="center">{contact.email}</TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    {isEpo ? (
                      <>
                        <IconButton size="small" onClick={() => openEpoEdit(contact)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEpoDelete(contact.id)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => handleSaveTicket(contact.id)}>
                          <WhatsAppIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => hadleEditContact(contact.id)}>
                          <EditIcon />
                        </IconButton>
                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setConfirmOpen(true);
                                setDeletingContact(contact);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          )}
                        />
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={isEpo ? 8 : 3} />}
            </>
          </TableBody>
          </Table>
        )}
      </Paper>

      {isEpo && (
        <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{i18n.t("epoContacts.sendVCards.dialogTitle")}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              {i18n.t("epoContacts.sendVCards.selectedCount", { count: selectedIds.size })}
            </Typography>
            <FormControl variant="outlined" size="small" className={classes.destinationDialogField}>
              <InputLabel>{i18n.t("epoContacts.sendVCards.destinationLabel")}</InputLabel>
              <Select
                value={sendDestination}
                onChange={(e) => setSendDestination(e.target.value)}
                label={i18n.t("epoContacts.sendVCards.destinationLabel")}
              >
                <MenuItem value="manager">{i18n.t("epoContacts.sendVCards.destinations.manager")}</MenuItem>
                <MenuItem value="scouting-group">{i18n.t("epoContacts.sendVCards.destinations.scoutingGroup")}</MenuItem>
                <MenuItem value="management-group">{i18n.t("epoContacts.sendVCards.destinations.managementGroup")}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendDialogOpen(false)} disabled={sendingVCards}>
              {i18n.t("epoContacts.form.buttons.cancel")}
            </Button>
            <Button
              onClick={handleSendVCards}
              color="primary"
              variant="contained"
              disabled={selectedIds.size === 0 || sendingVCards}
            >
              {sendingVCards ? <CircularProgress size={18} color="inherit" /> : i18n.t("epoContacts.sendVCards.confirmButton")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isEpo && (
        <Dialog open={epoModalOpen} onClose={handleEpoClose} maxWidth="md" fullWidth scroll="paper">
          <DialogTitle>
            {editingContact
              ? i18n.t("epoContacts.form.title.edit")
              : i18n.t("epoContacts.form.title.add")}
          </DialogTitle>
          <DialogContent dividers>
            <div className={classes.formGrid}>
              <div className={classes.avatarContainer} onClick={handlePhotoClick}>
                <Avatar src={photoPreview || undefined} className={classes.avatarLarge} />
                <div className={classes.cameraOverlay}>
                  <CameraAltIcon style={{ color: "#fff", fontSize: 16 }} />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handlePhotoChange}
                />
              </div>
              <TextField
                label={i18n.t("epoContacts.form.name")}
                value={epoForm.name}
                onChange={handleEpoFieldChange("name")}
                variant="outlined"
                size="small"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.lastName")}
                value={epoForm.lastName}
                onChange={handleEpoFieldChange("lastName")}
                variant="outlined"
                size="small"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.number")}
                value={epoForm.number}
                onChange={handleEpoFieldChange("number")}
                variant="outlined"
                size="small"
                fullWidth
                helperText="Con prefisso internazionale, senza + (es. IT: 39331…, UK: 44791…, US: 1212…)"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.email")}
                value={epoForm.email}
                onChange={handleEpoFieldChange("email")}
                variant="outlined"
                size="small"
                type="email"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.country")}
                value={epoForm.country}
                onChange={handleEpoFieldChange("country")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.city")}
                value={epoForm.city}
                onChange={handleEpoFieldChange("city")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.address")}
                value={epoForm.address}
                onChange={handleEpoFieldChange("address")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.zip")}
                value={epoForm.zip}
                onChange={handleEpoFieldChange("zip")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.jobTitle")}
                value={epoForm.jobTitle}
                onChange={handleEpoFieldChange("jobTitle")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={i18n.t("epoContacts.form.company")}
                value={epoForm.company}
                onChange={handleEpoFieldChange("company")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("epoContacts.form.tipologia")}</InputLabel>
                <Select
                  value={epoForm.tipologia}
                  onChange={handleEpoFieldChange("tipologia")}
                  label={i18n.t("epoContacts.form.tipologia")}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {tipologieOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("epoContacts.form.projects")}</InputLabel>
                <Select
                  multiple
                  value={epoForm.projectIds}
                  onChange={handleEpoFieldChange("projectIds")}
                  label={i18n.t("epoContacts.form.projects")}
                  renderValue={(selected) =>
                    projectOptions
                      .filter((project) => selected.includes(project.id))
                      .map((project) => project.name)
                      .join(", ")
                  }
                >
                  {projectOptions.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      <Checkbox checked={epoForm.projectIds.includes(project.id)} size="small" />
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={epoForm.isPartnerFidelizzato}
                    onChange={handleEpoFieldChange("isPartnerFidelizzato")}
                    color="primary"
                  />
                }
                label={i18n.t("epoContacts.form.isPartnerFidelizzato")}
              />
              <TextField
                className={classes.fullSpan}
                label={i18n.t("epoContacts.form.linkedinUrl")}
                value={epoForm.linkedinUrl}
                onChange={handleEpoFieldChange("linkedinUrl")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="https://linkedin.com/in/..."
              />
              {epoForm.isPartnerFidelizzato && (
                <TextField
                  className={classes.fullSpan}
                  label={i18n.t("epoContacts.form.description")}
                  value={epoForm.description}
                  onChange={handleEpoFieldChange("description")}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
              {epoForm.isPartnerFidelizzato && (
                <TextField
                  className={classes.fullSpan}
                  label={i18n.t("epoContacts.form.internalReferent")}
                  value={epoForm.internalReferent}
                  onChange={handleEpoFieldChange("internalReferent")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  placeholder="Es. Mario Rossi"
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEpoClose} disabled={saving}>
              {i18n.t("epoContacts.form.buttons.cancel")}
            </Button>
            <Button
              onClick={handleEpoSave}
              color="primary"
              variant="contained"
              disabled={saving || !epoForm.name || !epoForm.lastName || (!epoForm.number && !epoForm.email)}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                i18n.t("epoContacts.form.buttons.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </MainContainer>
  );
};

export default Contacts;
